import { useState, useEffect } from 'react'
import { X, MapPin, Sparkles, Loader2, HelpCircle } from '@/ui/icons'
import { Button } from '@/ui/components'
import { Input } from '@/ui/components'
import { Label } from '@/ui/components'
import { Textarea } from '@/ui/components'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/ui/components/primitives/tooltip'
import { GENERATE_TRIP_DESCRIPTION_TOOLTIP_COPY } from '@/shared/copy/tooltips'
import { TripDateQuickSelect, type QuickSelectPreset } from '../forms/TripDateQuickSelect'
import { TripDateRangePicker } from '../forms/TripDateRangePicker'
import { validateDateRange } from '../../utils/dateHelpers'
import { useGenerateTripDescription } from '../../hooks/useGenerateTripDescription'

interface TripEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (tripData: {
    name: string
    description?: string
    start_date?: string
    end_date?: string
    crew_limit?: number | null
    radio_channel?: string
  }) => void
  currentTrip?: any // Flexible type to accept any trip object
  isLoading?: boolean
}

export function TripEditModal({ 
  isOpen, 
  onClose, 
  onSave, 
  currentTrip,
  isLoading = false
}: TripEditModalProps) {
  const [name, setName] = useState(currentTrip?.name || '')
  const [description, setDescription] = useState(currentTrip?.description || '')
  const [startDate, setStartDate] = useState(currentTrip?.start_date || '')
  const [endDate, setEndDate] = useState(currentTrip?.end_date || '')
  const [crewLimit, setCrewLimit] = useState<number | null>(currentTrip?.crew_limit ?? null)
  const [radioChannel, setRadioChannel] = useState(currentTrip?.radio_channel ?? '')
  const [selectedPreset, setSelectedPreset] = useState<QuickSelectPreset>(null)
  const [dateError, setDateError] = useState<string | null>(null)
  const [descriptionSentence, setDescriptionSentence] = useState('')
  const { generate, isGenerating: isGeneratingDescription, error: descriptionGenError, clearError, canUseRanger } =
    useGenerateTripDescription()

  // Update form when currentTrip changes
  useEffect(() => {
    if (currentTrip) {
      setName(currentTrip.name || '')
      setDescription(currentTrip.description ?? '')
      setStartDate(currentTrip.start_date ?? '')
      setEndDate(currentTrip.end_date ?? '')
      setCrewLimit(currentTrip.crew_limit ?? null)
      setRadioChannel(currentTrip.radio_channel ?? '')
    }
  }, [currentTrip])

  useEffect(() => {
    if (isOpen) {
      setDescriptionSentence('')
      clearError()
    }
  }, [isOpen, clearError])

  const handleGenerateTripDescription = async () => {
    if (!descriptionSentence.trim()) return
    clearError()
    const tripId = currentTrip?.id as string | undefined
    const result = await generate({
      sentence: descriptionSentence,
      tripId,
      tripName: name.trim() || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    })
    if (result) {
      setDescription(result.slice(0, 500))
    }
  }

  const handleStartDateChange = (date: string) => {
    setStartDate(date)
    setSelectedPreset(null)
    validateDates(date, endDate)
  }

  const handleEndDateChange = (date: string) => {
    setEndDate(date)
    setSelectedPreset(null)
    validateDates(startDate, date)
  }

  const validateDates = (start: string, end: string) => {
    if (start && end) {
      const validation = validateDateRange(start, end)
      setDateError(validation.isValid ? null : validation.error || null)
    } else {
      setDateError(null)
    }
  }

  const handleQuickSelect = (start: string, end: string) => {
    setStartDate(start)
    setEndDate(end)
    setDateError(null)
  }

  const handlePresetChange = (preset: QuickSelectPreset) => {
    setSelectedPreset(preset)
  }

  const handleSave = () => {
    if (!name.trim()) {
      alert('Trip name is required')
      return
    }

    // Validate description length
    if (description && description.length > 500) {
      alert('Description must be 500 characters or less')
      return
    }

    // Validate date range before saving
    if (startDate && endDate) {
      const validation = validateDateRange(startDate, endDate)
      if (!validation.isValid) {
        setDateError(validation.error || 'Invalid date range')
        return
      }
    }

    onSave({
      name: name.trim(),
      description: description.trim(),
      // Dates are optional, and an empty string fails the API's date format check.
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      crew_limit: crewLimit,
      radio_channel: radioChannel.trim() || undefined
    })
  }

  if (!isOpen) return null

  const showRangerDescriptionEditor = canUseRanger && Boolean(description.trim())
  const descriptionLabelFor =
    !canUseRanger || showRangerDescriptionEditor ? 'description' : 'trip-desc-seed'

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Edit Trip Details</h2>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isLoading}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="name" className="flex items-center space-x-2">
              <MapPin className="w-4 h-4" />
              <span>Trip Name *</span>
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Moab Adventure 2025"
              className="mt-1"
              required
            />
          </div>

          <div>
            <Label htmlFor={descriptionLabelFor}>Description</Label>
            {canUseRanger ? (
              <>
                <div className="mt-1 space-y-2">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <div className="flex-1 space-y-1">
                      <Label htmlFor="trip-desc-seed" className="text-xs font-normal text-muted-foreground">
                        Start from one sentence
                      </Label>
                      <Input
                        id="trip-desc-seed"
                        value={descriptionSentence}
                        onChange={(e) => {
                          clearError()
                          setDescriptionSentence(e.target.value)
                        }}
                        placeholder="e.g., Long weekend in the Sawtooths with the crew"
                        disabled={isLoading || isGeneratingDescription}
                        className="min-h-[44px]"
                      />
                    </div>
                    <div className="flex items-center gap-1 shrink-0 pb-0.5">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleGenerateTripDescription}
                        disabled={
                          !descriptionSentence.trim() || isLoading || isGeneratingDescription
                        }
                        className="text-xs text-earth-700 dark:text-foreground"
                      >
                        {isGeneratingDescription ? (
                          <>
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-3 h-3 mr-1" />
                            Generate description
                          </>
                        )}
                      </Button>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            aria-label="How does generate description work?"
                          >
                            <HelpCircle className="w-3.5 h-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="end" className="max-w-xs">
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {GENERATE_TRIP_DESCRIPTION_TOOLTIP_COPY}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                  {descriptionGenError && (
                    <p className="text-sm text-destructive">{descriptionGenError}</p>
                  )}
                </div>
                {showRangerDescriptionEditor && (
                  <>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Edit your trip description..."
                      className="mt-2"
                      rows={4}
                      maxLength={500}
                    />
                    <div
                      className={`text-xs mt-1 ${
                        (description?.length || 0) > 450
                          ? 'text-warning'
                          : (description?.length || 0) >= 500
                            ? 'text-destructive'
                            : 'text-muted-foreground'
                      }`}
                    >
                      {(description?.length || 0)}/500 characters
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your trip..."
                  className="mt-1"
                  rows={4}
                  maxLength={500}
                />
                <div
                  className={`text-xs mt-1 ${
                    (description?.length || 0) > 450
                      ? 'text-warning'
                      : (description?.length || 0) >= 500
                        ? 'text-destructive'
                        : 'text-muted-foreground'
                  }`}
                >
                  {(description?.length || 0)}/500 characters
                </div>
              </>
            )}
          </div>

          <div>
            <Label htmlFor="crew_limit">
              Crew Limit (optional)
            </Label>
            <Input
              id="crew_limit"
              type="number"
              min="1"
              value={crewLimit || ''}
              onChange={(e) => {
                const value = e.target.value
                setCrewLimit(value === '' ? null : parseInt(value, 10))
              }}
              placeholder="e.g., 5"
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Maximum number of vehicles/crew members for this trip based on campsite capacity. Leave empty for unlimited.
            </p>
          </div>

          <div>
            <Label htmlFor="radio_channel">Radio channel (optional)</Label>
            <Input
              id="radio_channel"
              placeholder="e.g., Channel 4"
              value={radioChannel}
              onChange={(e) => setRadioChannel(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="trip_dates">
              Trip Dates
            </Label>
            
            <div className="space-y-4 mt-1">
              <TripDateQuickSelect
                onDateSelect={handleQuickSelect}
                selectedPreset={selectedPreset}
                onPresetChange={handlePresetChange}
              />
              
              <TripDateRangePicker
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
              />
              
              {dateError && (
                <p className="text-sm text-red-600 mt-1">{dateError}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 mt-6">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading || !name.trim()}>
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  )
}

